import { fetchApi } from './apiClient';
import * as Sentry from '@sentry/react'; // 导入 Sentry

// 从环境变量获取后端日志端点和 API 基地址，提供默认值
const logEndpoint = import.meta.env.VITE_BACKEND_LOG_ENDPOINT || '/api/log';
const API_BASE_URL = import.meta.env.VITE_BACKEND_API_URL || '';
const dev = import.meta.env.DEV;

/**
 * 将日志发送到后端代理接口
 * @param level 日志级别 ('info', 'warn', 'error', 'debug')
 * @param message 日志消息
 * @param data 附加的结构化数据
 */
const sendLogToServer = (level: string, message: string, data: object = {}): void => {
  if(!dev){
    return;
  }

  const logEntry = {
    level,
    message,
    // 注意：这里不加 service: 'web'，因为后端代理会自动添加 source: 'frontend'
    url: window.location.href, // 添加当前页面 URL 上下文
    timestamp: new Date().toISOString(), // 添加前端时间戳
    ...data, // 合并传入的附加数据
  };

  // 构建完整的 URL，确保没有双斜杠
  const url = API_BASE_URL
    ? `${API_BASE_URL.replace(/\/$/, '')}${logEndpoint.startsWith('/') ? logEndpoint : `/${logEndpoint}`}`
    : logEndpoint;

  // 使用原生 fetch API 异步发送，并捕获错误
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // 包含凭证（cookies）
    body: JSON.stringify(logEntry),
  }).catch((err: Error) => {
    // 如果发送到后端失败，则在浏览器 *原始* 控制台记录错误和原始日志，避免循环
    console.error('[Logger] Failed to send log to backend via', url, ':', err);
    console.error('[Original Log] ', logEntry);
  });
};

/**
 * 前端日志记录器，用于将选择性的关键日志发送到后端
 */
export const logger = {
  /**
   * 记录信息级别日志到后端
   */
  info: (message: string, data?: object): void => {
    sendLogToServer('info', message, data);
    // 同时在浏览器控制台打印，方便本地调试
    console.log(`[INFO] ${message}`, data || '');
  },

  /**
   * 记录警告级别日志到后端
   */
  warn: (message: string, data?: object): void => {
    sendLogToServer('warn', message, data);
    console.warn(`[WARN] ${message}`, data || '');
  },

  /**
   * 记录错误级别日志到后端和 Sentry
   */
  error: (message: string, error?: Error | any, data?: object): void => {
    const errorData: any = {};
    let capturedError = error; // 保留原始 error 用于 Sentry

    if (error) {
      if (error instanceof Error) {
        errorData.errorMessage = error.message;
        errorData.errorStack = error.stack;
        errorData.errorName = error.name;
      } else {
        try {
          // 尝试序列化未知错误类型
          errorData.errorDetails = JSON.parse(JSON.stringify(error));
          capturedError = new Error(String(error)); // 为 Sentry 创建一个 Error 对象
        } catch {
          errorData.errorDetails = String(error);
          capturedError = new Error(String(error)); // 为 Sentry 创建一个 Error 对象
        }
      }
    } else {
      capturedError = new Error(message); // 如果没有提供 error 对象，为 Sentry 创建一个
    }

    // 1. 发送到后端代理
    sendLogToServer('error', message, { ...errorData, ...data });

    // 2. 发送到 Sentry
    Sentry.withScope(scope => {
      if (data) {
        scope.setContext("Custom Data", data as { [key: string]: any });
      }
      scope.setLevel("error");
      if (capturedError instanceof Error) {
        Sentry.captureException(capturedError);
      } else {
        Sentry.captureMessage(message);
      }

    });

    // 3. 打印到浏览器控制台
    console.error(`[ERROR] ${message}`, error, data || '');
  },

  /**
   * 记录调试级别日志 (默认只在控制台打印，可选发送)
   */
  debug: (message: string, data?: { sendToServer?: boolean } & Record<string, any>): void => {
    console.debug(`[DEBUG] ${message}`, data || '');
    if (data?.sendToServer) {
      const { sendToServer, ...restData } = data; // 移除特殊标志
      sendLogToServer('debug', message, restData);
    }
  },
};

// 导出原始 console 以便明确区分
export const consoleLog = console;
